import React, { useState, useRef, useCallback, useEffect } from 'react';
import { generateMotionControlVideo, getMotionControlModels } from '../services/aiService';
import { uploadDataUriToBackend } from '../services/backendService';
import { LoadingSpinner } from './icons/LoadingSpinner';
import { CloseIcon } from './icons/CloseIcon';
import { UploadIcon } from './icons/UploadIcon';
import { SaveIcon } from './icons/SaveIcon';
import { DownloadIcon } from './icons/DownloadIcon';
import { MagicWandIcon } from './icons/MagicWandIcon';

export const AnimateVideoModal = ({ shot, projectData, onClose, onSave, onDeductCredits, onTrackCreditsUsed }) => {
    const [referenceVideo, setReferenceVideo] = useState(null);
    const [generatedVideoUrl, setGeneratedVideoUrl] = useState(null);
    const [status, setStatus] = useState('idle');
    const [progressMessage, setProgressMessage] = useState('');
    const [error, setError] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [animationPrompt, setAnimationPrompt] = useState(
        shot?.description || 'Apply the motion from the reference video to this image'
    );
    const [resolution, setResolution] = useState('480p');
    const [motionModels, setMotionModels] = useState([]);
    const [selectedModel, setSelectedModel] = useState('');
    const [loadingModels, setLoadingModels] = useState(true);

    const fileInputRef = useRef(null);

    // Fetch motion control models on mount
    useEffect(() => {
        let cancelled = false;
        setLoadingModels(true);
        getMotionControlModels().then(models => {
            if (cancelled) return;
            setMotionModels(models);
            if (models.length > 0) setSelectedModel(models[0].key || models[0].model_key || models[0].id || '');
            setLoadingModels(false);
        }).catch(() => {
            if (!cancelled) setLoadingModels(false);
        });
        return () => { cancelled = true; };
    }, []);

    const handleFile = (file) => {
        if (file && file.type.startsWith('video/')) {
            const MAX_SIZE_MB = 7;
            if (file.size > MAX_SIZE_MB * 1024 * 1024) {
                setError(`Reference video is too large. Please upload a file smaller than ${MAX_SIZE_MB}MB.`);
                return;
            }
            setError(null);
            const reader = new FileReader();
            reader.onloadend = () => setReferenceVideo({ name: file.name, data: reader.result });
            reader.readAsDataURL(file);
        } else {
            setError("Invalid file type. Please upload a video file.");
        }
    };

    const handleFileChange = (e) => { const file = e.target.files?.[0]; if (file) handleFile(file); };

    const handleDrop = useCallback((e) => {
        e.preventDefault(); e.stopPropagation(); setIsDragging(false);
        const file = e.dataTransfer.files?.[0]; if (file) handleFile(file);
    }, []);

    const handleDragEvents = (e, isEntering) => {
        e.preventDefault(); e.stopPropagation();
        setIsDragging(isEntering);
    };

    const handleGenerate = async () => {
        if (!shot?.startFrameImage) {
            setError("A start frame image is required.");
            return;
        }
        setStatus('generating'); setError(null);
        setProgressMessage('Starting motion control generation...');
        try {
            // Upload reference video to get a public URL (backend expects HTTP URLs, not base64)
            let motionVideoUrl = null;
            if (referenceVideo?.data) {
                setProgressMessage('Uploading reference video...');
                const userEmail = sessionStorage.getItem('userEmail');
                motionVideoUrl = await uploadDataUriToBackend(referenceVideo.data, userEmail);
                console.log('📹 Reference video uploaded:', motionVideoUrl);
            }

            setProgressMessage('Generating motion control video...');
            const result = await generateMotionControlVideo(
                shot.startFrameImage,
                motionVideoUrl,
                animationPrompt,
                projectData?.aspectRatio || '16:9',
                setProgressMessage,
                selectedModel || undefined
            );
            const videoUrl = result?.videoUrl || result;
            setGeneratedVideoUrl(videoUrl);
            setStatus('done');
            if (onTrackCreditsUsed && result?.creditsUsed) {
                onTrackCreditsUsed(result.creditsUsed, result.creditsRemaining, result.jobId);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "An unknown error occurred.");
            setStatus('error');
        }
    };

    const handleSaveToProject = () => { if (generatedVideoUrl) onSave(shot.id, generatedVideoUrl); };

    const handleDownload = async () => {
        if (!generatedVideoUrl) return;
        try {
            const response = await fetch(generatedVideoUrl);
            if (!response.ok) throw new Error('Failed to fetch video.');
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = `loopai_motion_${shot.id}.mp4`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
        } catch (e) { setError(`Download failed: ${e.message}`); }
    };

    const getModelDisplayName = (model) => model.name || model.display_name || model.provider || model.key || 'Unknown';
    const getModelCost = (model) => model.cost_estimate || null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-3" onClick={onClose} data-testid="motion-control-overlay">
            <div className="bg-[#12121a] border border-gray-700/60 rounded-xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-testid="motion-control-modal">

                {/* Header */}
                <div className="flex justify-between items-center px-5 py-3 border-b border-gray-800">
                    <div>
                        <h2 className="text-base font-semibold text-gray-100" data-testid="motion-title">Motion Control</h2>
                        <p className="text-xs text-gray-500">Generate video from frame image with motion reference</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-700/60 transition-colors" data-testid="motion-close-btn">
                        <CloseIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5">
                    {status === 'generating' ? (
                        <div className="flex flex-col items-center justify-center h-64 gap-3">
                            <LoadingSpinner className="w-10 h-10" />
                            <h3 className="text-lg font-semibold">Generating Motion Video...</h3>
                            <p className="text-purple-400 text-sm">{progressMessage}</p>
                        </div>
                    ) : status === 'done' && generatedVideoUrl ? (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Source frame (left) */}
                                <div>
                                    <label className="text-xs text-gray-500 mb-1.5 block font-medium uppercase tracking-wide">Source Frame</label>
                                    <div className="aspect-video bg-black rounded-lg overflow-hidden">
                                        <img src={shot.startFrameImage} alt="Frame" className="w-full h-full object-contain" crossOrigin="anonymous" referrerPolicy="no-referrer" />
                                    </div>
                                </div>
                                {/* Generated video (right) */}
                                <div>
                                    <label className="text-xs text-emerald-400 mb-1.5 block font-medium uppercase tracking-wide">Generated Video</label>
                                    <video src={generatedVideoUrl} controls autoPlay loop className="w-full rounded-lg aspect-video object-contain bg-black" data-testid="motion-result-video" />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {/* Source Frame */}
                                <div>
                                    <label className="text-xs text-gray-500 mb-1.5 block font-medium uppercase tracking-wide">Source Frame</label>
                                    <div className="aspect-video bg-black rounded-lg flex items-center justify-center overflow-hidden border border-gray-700/60">
                                        {shot?.startFrameImage ? (
                                            <img src={shot.startFrameImage} alt="Start frame" className="w-full h-full object-contain" crossOrigin="anonymous" referrerPolicy="no-referrer" />
                                        ) : (
                                            <p className="text-gray-600 text-sm">No start frame available</p>
                                        )}
                                    </div>
                                </div>

                                {/* Reference Motion Video */}
                                <div>
                                    <label className="text-xs text-gray-500 mb-1.5 block font-medium uppercase tracking-wide">Reference Motion Video <span className="text-gray-600 normal-case">(optional)</span></label>
                                    <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="video/*" className="hidden" />
                                    {referenceVideo ? (
                                        <div className="space-y-2">
                                            <video src={referenceVideo.data} controls className="w-full rounded-lg aspect-video object-contain bg-black border border-gray-700/60" data-testid="motion-ref-video" />
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-gray-400 truncate flex-1">{referenceVideo.name}</span>
                                                <button onClick={() => setReferenceVideo(null)}
                                                    className="text-xs px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-md text-gray-300 transition-colors" data-testid="motion-remove-ref">
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            onDrop={handleDrop}
                                            onDragOver={e => handleDragEvents(e, true)}
                                            onDragEnter={e => handleDragEvents(e, true)}
                                            onDragLeave={e => handleDragEvents(e, false)}
                                            onClick={() => fileInputRef.current?.click()}
                                            className={`aspect-video border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-all
                                                ${isDragging ? 'border-purple-500 bg-purple-500/10' : 'border-gray-700 hover:border-gray-500 hover:bg-gray-800/30'}`}
                                            data-testid="motion-upload-zone"
                                        >
                                            <UploadIcon className="w-8 h-8 text-gray-600 mb-2" />
                                            <p className="text-gray-400 text-sm">Drag & drop motion video</p>
                                            <p className="text-gray-600 text-xs mt-0.5">or click to browse (max 7MB)</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Controls row */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {/* Model Selection */}
                                <div>
                                    <label className="text-xs text-gray-500 mb-1.5 block font-medium uppercase tracking-wide">Motion Control Model</label>
                                    {loadingModels ? (
                                        <div className="flex items-center gap-2 py-2 text-xs text-gray-500">
                                            <div className="w-4 h-4 border border-purple-500 border-t-transparent rounded-full animate-spin" />
                                            Loading models...
                                        </div>
                                    ) : motionModels.length > 0 ? (
                                        <div className="space-y-1.5">
                                            {motionModels.map(model => {
                                                const key = model.key || model.model_key || model.id || '';
                                                const isSelected = selectedModel === key;
                                                return (
                                                    <button key={key} onClick={() => setSelectedModel(key)}
                                                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-2
                                                            ${isSelected
                                                                ? 'bg-purple-600/20 border border-purple-500/50 text-purple-300'
                                                                : 'bg-gray-800/60 border border-gray-700/60 text-gray-400 hover:bg-gray-700/60 hover:text-gray-200'}`}
                                                        data-testid={`model-${key}`}>
                                                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isSelected ? 'bg-purple-400' : 'bg-gray-600'}`} />
                                                        <div className="flex-1 min-w-0">
                                                            <span className="truncate block">{getModelDisplayName(model)}</span>
                                                            {getModelCost(model) && <span className="text-[10px] text-gray-500 block">{getModelCost(model)}</span>}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-gray-600 py-2">No motion control models available. The default video model will be used.</p>
                                    )}
                                </div>

                                {/* Resolution + Prompt */}
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1.5 block font-medium uppercase tracking-wide">Resolution</label>
                                        <div className="flex gap-1.5">
                                            {['480p', '580p', '720p'].map(r => (
                                                <button key={r} onClick={() => setResolution(r)}
                                                    className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors
                                                        ${resolution === r ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
                                                    data-testid={`resolution-${r}`}>{r}</button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 mb-1.5 block font-medium uppercase tracking-wide">Animation Prompt</label>
                                        <textarea
                                            value={animationPrompt}
                                            onChange={e => setAnimationPrompt(e.target.value)}
                                            placeholder="Describe the motion or animation you want..."
                                            rows={3}
                                            className="w-full p-2.5 bg-gray-800/80 border border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
                                            data-testid="motion-prompt"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="mt-4 bg-red-500/10 border border-red-500/40 text-red-400 p-3 rounded-lg text-sm" data-testid="motion-error">{error}</div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-gray-800 flex justify-between items-center">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors" data-testid="motion-cancel-btn">Close</button>
                    <div className="flex items-center gap-2">
                        {status === 'done' && (
                            <>
                                <button onClick={handleDownload} className="px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium rounded-lg flex items-center gap-1.5" data-testid="motion-download-btn">
                                    <DownloadIcon className="w-4 h-4" /> Download
                                </button>
                                <button onClick={handleSaveToProject} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg flex items-center gap-1.5" data-testid="motion-save-btn">
                                    <SaveIcon className="w-4 h-4" /> Save to Project
                                </button>
                            </>
                        )}
                        <button onClick={handleGenerate}
                            disabled={!shot?.startFrameImage || status === 'generating'}
                            className="px-5 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm font-semibold rounded-lg flex items-center gap-1.5 transition-colors"
                            data-testid="motion-generate-btn">
                            <MagicWandIcon className="w-4 h-4" />
                            {status === 'done' ? 'Re-generate' : 'Generate Video'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
